/**
 * 收款入口 API（payment-entries-links-and-qr:ADM-10）。
 *
 * 管理端收款入口对话框用：按订单查询既有入口、生成、查看详情、修改分期授权、转发审计。
 * 字段权威：`prototype/shared/src/types/domain.ts:436-473` + `src/types/payment.types.ts`。
 *
 * 真实后端 CreatePaymentEntryResult / PaymentEntryRecordDto 使用 entryId、derivedStatus、validUntil，
 * 与对话框 PaymentEntryView（id / status / createdAt）不同形；本层映射后再交给 UI。
 *
 * - POST   /payment-entries                  -> 生成入口（无金额字段，幂等）
 * - GET    /payment-entries?orderId=         -> 按订单列入口（对话框/员工查看）
 * - GET    /payment-entries/:id              -> 入口详情（派生态实时）
 * - POST   /payment-entries/:id/allow-deposit-> 修改分期授权（仅首笔未支付）
 * - POST   /payment-entries/:id/forward-audit-> 转发审计
 */
import { ApiPaths } from './api.config';
import { http } from './transport';
import type {
  CreatePaymentEntryPayload,
  MockPayPaymentEntryResult,
  PaymentEntryListQuery,
  PaymentEntryStatus,
  PaymentEntryType,
  PaymentEntryView,
  UpdateAllowDepositPayload,
} from '../types/payment.types';

const STATUS_ALIASES: Record<string, PaymentEntryStatus> = {
  WAITING_SCAN: 'WAITING_SCAN',
  WAITING_PAYMENT: 'WAITING_PAYMENT',
  PAID: 'PAID',
  CLOSED: 'CLOSED',
};

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asStatus(value: unknown): PaymentEntryStatus {
  if (typeof value === 'string' && value in STATUS_ALIASES) {
    return STATUS_ALIASES[value]!;
  }
  return 'WAITING_SCAN';
}

/**
 * 将生成结果或列表/详情 DTO 规范为对话框视图。
 * 兼容 mock（id/status/createdAt）与真实后端（entryId/derivedStatus/validUntil）。
 */
export function toPaymentEntryView(raw: unknown): PaymentEntryView {
  const record = (raw ?? {}) as Record<string, unknown>;
  const derived = asStatus(record.derivedStatus ?? record.status);
  const createdAt = asString(record.createdAt) || asString(record.validUntil);
  const receivablePlanId = asString(record.receivablePlanId);
  const summaryRaw = record.receivablePlanSummary;
  const summary =
    summaryRaw && typeof summaryRaw === 'object'
      ? (summaryRaw as PaymentEntryView['receivablePlanSummary'])
      : undefined;

  return {
    id: asString(record.id || record.entryId),
    orderId: asString(record.orderId),
    receivablePlanId: receivablePlanId || undefined,
    studentId: asString(record.studentId),
    entryType: record.entryType === 'QR' ? 'QR' : ('LINK' as PaymentEntryType),
    amount: asNumber(record.amount),
    allowDeposit: Boolean(record.allowDeposit),
    guardianMobileHash: asString(record.guardianMobileHash),
    token: asString(record.token),
    createdByStaffId: asString(record.createdByStaffId),
    campusId: asString(record.campusId),
    status: asStatus(record.status ?? record.derivedStatus),
    createdAt,
    openCount: asNumber(record.openCount) ?? 0,
    lastOpenedAt: asString(record.lastOpenedAt) || undefined,
    derivedStatus: derived,
    validUntil: asString(record.validUntil) || createdAt,
    createdByName: asString(record.createdByName),
    studentName: asString(record.studentName),
    campusName: asString(record.campusName),
    receivablePlanSummary: summary,
    paymentChannel: record.paymentChannel === 'real' ? 'real' : (record.paymentChannel === 'mock' ? 'mock' : undefined),
    qrContent: asString(record.qrContent) || undefined,
    qrImageUrl: asString(record.qrImageUrl) || undefined,
  };
}

function buildListQuery(query: PaymentEntryListQuery): string {
  const params = new URLSearchParams();
  params.set('orderId', query.orderId);
  return `${ApiPaths.paymentEntries.list}?${params.toString()}`;
}

async function listPaymentEntries(
  query: PaymentEntryListQuery,
): Promise<PaymentEntryView[]> {
  const data = await http.get<unknown>(buildListQuery(query));
  return Array.isArray(data) ? data.map(toPaymentEntryView) : [];
}

async function getPaymentEntry(id: string): Promise<PaymentEntryView> {
  return toPaymentEntryView(await http.get<unknown>(ApiPaths.paymentEntries.detail(id)));
}

async function createPaymentEntry(
  payload: CreatePaymentEntryPayload,
): Promise<PaymentEntryView> {
  return toPaymentEntryView(
    await http.post<unknown>(ApiPaths.paymentEntries.create, payload),
  );
}

async function updateAllowDeposit(
  id: string,
  payload: UpdateAllowDepositPayload,
): Promise<PaymentEntryView> {
  return toPaymentEntryView(
    await http.post<unknown>(
      ApiPaths.paymentEntries.updateAllowDeposit(id),
      payload,
    ),
  );
}

async function recordForwardAudit(id: string): Promise<{ entryId: string }> {
  return http.post<{ entryId: string }>(
    ApiPaths.paymentEntries.forwardAudit(id),
    {},
  );
}

/** 模拟家长已支付（IKBFM3 mock 通道演示：全链路复用生产结算，幂等）。 */
async function mockPayPaymentEntry(id: string): Promise<MockPayPaymentEntryResult> {
  return http.post<MockPayPaymentEntryResult>(ApiPaths.paymentEntries.mockPaid(id), {});
}

/** 收款入口 API 唯一对外入口。 */
export const paymentEntriesApi = {
  listPaymentEntries,
  getPaymentEntry,
  createPaymentEntry,
  updateAllowDeposit,
  recordForwardAudit,
  mockPayPaymentEntry,
};
